import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../mock/mock";
import { toast } from "sonner";
import { Eye, EyeOff, Check, X, ArrowRight, Mail, Lock, User } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../hooks/useAuth";

/* ============ Shared helpers ============ */
const useFadeIn = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setInView(true); return; }
    const t = requestAnimationFrame(() => setInView(true));
    return () => cancelAnimationFrame(t);
  }, []);
  return { ref, inView };
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const passwordRules = (v = "") => ({
  length: v.length >= 8,
  upper: /[A-Z]/.test(v),
  lower: /[a-z]/.test(v),
  number: /\d/.test(v),
  special: /[^A-Za-z0-9]/.test(v),
});
const passwordStrong = (v) => Object.values(passwordRules(v)).every(Boolean);

/* ============ Auth Shell ============ */
export const AuthShell = ({ children }) => {
  const { inView } = useFadeIn();
  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] bg-[#bcd3ff] rounded-full blur-3xl opacity-80" />
        <div className="absolute top-24 -right-40 w-[560px] h-[560px] bg-[#d5e4ff] rounded-full blur-3xl opacity-90" />
      </div>
      <div className="min-h-screen flex items-center justify-center px-6 py-14">
        <div
          className={`w-full max-w-[460px] transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div
            className="rounded-[28px] bg-white border border-slate-100 shadow-[0_45px_120px_-25px_rgba(29,78,216,0.4)] p-8 md:p-10 hover:shadow-[0_55px_140px_-25px_rgba(29,78,216,0.5)] transition-shadow duration-500"
            data-testid="auth-card"
          >
            <Link to="/" className="inline-flex items-center gap-2" data-testid="auth-logo">
              <img src={LOGO_URL} alt="Medily" className="h-8" />
            </Link>
            {/* Reserved space for future social login buttons */}
            <div className="h-2" aria-hidden data-testid="social-login-reserved-space" />
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
/* ============ Field ============ */
const Field = ({ label, error, children, testid }) => (
  <div className="space-y-1.5" data-testid={testid}>
    <label className="text-[12.5px] font-semibold text-slate-700 tracking-wide">{label}</label>
    {children}
    {error && <p className="text-[12px] text-rose-600" data-testid={`${testid}-error`}>{error}</p>}
  </div>
);

const inputClass =
  "w-full h-11 pl-10 pr-11 rounded-xl border border-slate-200 bg-white text-[14.5px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#1d4ed8] focus:ring-4 focus:ring-[#1d4ed8]/10 transition";

const IconInput = ({ Icon, right, ...props }) => (
  <div className="relative">
    <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    <input className={inputClass} {...props} />
    {right}
  </div>
);

const PasswordChecklist = ({ value }) => {
  const r = passwordRules(value);
  const items = [
    ["length", "Minimum 8 characters"],
    ["upper", "One uppercase letter"],
    ["lower", "One lowercase letter"],
    ["number", "One number"],
    ["special", "One special character"],
  ];
  return (
    <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="password-checklist">
      {items.map(([k, label]) => (
        <li key={k} className="flex items-center gap-2 text-[12px]" data-testid={`pw-rule-${k}`}>
          <span className={`w-4 h-4 rounded-full flex items-center justify-center transition-colors ${r[k] ? "bg-[#e6f7ee] text-[#15803d]" : "bg-slate-100 text-slate-400"}`}>
            {r[k] ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
          </span>
          <span className={r[k] ? "text-slate-700" : "text-slate-500"}>{label}</span>
        </li>
      ))}
    </ul>
  );
};

/* ============ LOGIN ============ */
export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [show, setShow] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const e = {};
    if (!email) e.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(email)) e.email = "Enter a valid email address.";
    if (!password) e.password = "Please enter your password.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back!", { description: "You are now signed in." });
      // Route based on Resources access: subscribers → dashboard, else → pricing.
      navigate(user?.has_resources_access ? "/dashboard" : "/pricing", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Login failed";
      setErrors({ form: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="mt-6 text-[28px] md:text-[32px] font-semibold text-slate-900 tracking-tight" data-testid="login-heading">
        Welcome back
      </h1>
      <p className="mt-2 text-[14.5px] text-slate-500">Sign in to continue your MMI preparation.</p>

      <form className="mt-7 space-y-4" onSubmit={onSubmit} noValidate data-testid="login-form">
        <Field label="Email address" error={errors.email} testid="login-email-field">
          <IconInput Icon={Mail} type="email" placeholder="you@example.com" value={email}
            onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="login-email-input" />
        </Field>

        <Field label="Password" error={errors.password} testid="login-password-field">
          <IconInput
            Icon={Lock}
            type={show ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            data-testid="login-password-input"
            right={
              <button type="button" onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1d4ed8] transition"
                aria-label={show ? "Hide password" : "Show password"} data-testid="login-password-toggle">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />
        </Field>

        <div className="flex items-center justify-between text-[13px]">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-[#1d4ed8] focus:ring-[#1d4ed8]/30" data-testid="login-remember" />
            <span className="text-slate-600">Remember me</span>
          </label>
        </div>

        {errors.form && (
          <p className="text-[13px] text-rose-600" data-testid="login-form-error">{errors.form}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_40px_-12px_rgba(29,78,216,0.65)] disabled:opacity-70"
          data-testid="login-submit"
        >
          Log In <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      <p className="mt-6 text-center text-[13.5px] text-slate-500">
        Don’t have an account?{" "}
        <Link to="/signup" className="text-[#1d4ed8] font-semibold hover:underline" data-testid="login-to-signup">Create one</Link>
      </p>
    </AuthShell>
  );
};

/* ============ SIGN UP ============ */
export const Signup = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [showC, setShowC] = useState(false);
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Preserve selected plan across sign-up flow (parked earlier by Pricing).
  const [selectedPlan, setSelectedPlan] = useState(null);
  useEffect(() => {
    try {
      const v = sessionStorage.getItem("medily.selectedPlan");
      if (v) setSelectedPlan(v);
    } catch (_) { /* noop */ }
  }, []);

  const validate = () => {
    const e = {};
    if (!firstName.trim()) e.firstName = "First name is required.";
    if (!lastName.trim()) e.lastName = "Last name is required.";
    if (!email) e.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(email)) e.email = "Enter a valid email address.";
    if (!password) e.password = "Please choose a password.";
    else if (!passwordStrong(password)) e.password = "Please meet all password requirements.";
    if (!confirm) e.confirm = "Please confirm your password.";
    else if (confirm !== password) e.confirm = "Passwords do not match.";
    if (!terms) e.terms = "Please accept the Terms & Privacy Policy to continue.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || submitting) return;
    setSubmitting(true);
    try {
      const name = `${firstName.trim()} ${lastName.trim()}`.trim();
      await register(name, email.trim().toLowerCase(), password);
      toast.success("Account created successfully.", {
        description: selectedPlan ? "Continuing to checkout…" : "You can pick a plan any time.",
      });
      // New accounts start with has_resources_access=false, so we always land them on /pricing.
      navigate("/pricing", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Sign-up failed";
      setErrors({ form: msg });
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const planLabel =
    selectedPlan === "resources" ? "Medily Resources"
    : selectedPlan === "mock" ? "University-Specific Mock MMI"
    : selectedPlan === "bundle" ? "Complete Preparation Bundle"
    : null;

  return (
    <AuthShell>
      <h1 className="mt-6 text-[28px] md:text-[32px] font-semibold text-slate-900 tracking-tight" data-testid="signup-heading">
        Create your account
      </h1>
      <p className="mt-2 text-[14.5px] text-slate-500">Start your Medily journey in just a few moments.</p>

      {planLabel && (
        <div className="mt-4 rounded-xl bg-[#eef4ff] border border-[#dbe6ff] px-3 py-2 text-[12.5px] text-[#1d4ed8] flex items-center gap-2" data-testid="signup-plan-badge">
          <Check className="w-3.5 h-3.5" />
          Selected plan: <span className="font-semibold">{planLabel}</span>
        </div>
      )}

      <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate data-testid="signup-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" error={errors.firstName} testid="signup-first-field">
            <IconInput Icon={User} type="text" placeholder="Sarah" value={firstName}
              onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" data-testid="signup-first-input" />
          </Field>
          <Field label="Last name" error={errors.lastName} testid="signup-last-field">
            <IconInput Icon={User} type="text" placeholder="Ahmed" value={lastName}
              onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" data-testid="signup-last-input" />
          </Field>
        </div>

        <Field label="Email address" error={errors.email} testid="signup-email-field">
          <IconInput Icon={Mail} type="email" placeholder="you@example.com" value={email}
            onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="signup-email-input" />
        </Field>

        <Field label="Password" error={errors.password} testid="signup-password-field">
          <IconInput
            Icon={Lock}
            type={show ? "text" : "password"}
            placeholder="Choose a strong password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            data-testid="signup-password-input"
            right={
              <button type="button" onClick={() => setShow((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1d4ed8] transition"
                aria-label={show ? "Hide password" : "Show password"} data-testid="signup-password-toggle">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />
          <PasswordChecklist value={password} />
        </Field>

        <Field label="Confirm password" error={errors.confirm} testid="signup-confirm-field">
          <IconInput
            Icon={Lock}
            type={showC ? "text" : "password"}
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            data-testid="signup-confirm-input"
            right={
              <button type="button" onClick={() => setShowC((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1d4ed8] transition"
                aria-label={showC ? "Hide password" : "Show password"}>
                {showC ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            }
          />
        </Field>

        <div>
          <label className="inline-flex items-start gap-2 cursor-pointer text-[13px] text-slate-600">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#1d4ed8] focus:ring-[#1d4ed8]/30" data-testid="signup-terms" />
            <span>
              I agree to the <Link to="/terms-and-conditions" className="text-[#1d4ed8] font-medium hover:underline" data-testid="signup-terms-link">Terms &amp; Conditions</Link>{" "}
              and <Link to="/privacy-policy" className="text-[#1d4ed8] font-medium hover:underline" data-testid="signup-privacy-link">Privacy Policy</Link>.
            </span>
          </label>
          {errors.terms && <p className="text-[12px] text-rose-600 mt-1" data-testid="signup-terms-error">{errors.terms}</p>}
        </div>

        {errors.form && (
          <p className="text-[13px] text-rose-600" data-testid="signup-form-error">{errors.form}</p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1d4ed8] text-white font-medium hover:bg-[#1e40af] transition shadow-[0_18px_40px_-12px_rgba(29,78,216,0.65)] disabled:opacity-70"
          data-testid="signup-submit"
        >
          Create Account <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      <p className="mt-6 text-center text-[13.5px] text-slate-500">
        Already have an account?{" "}
        <Link to="/login" className="text-[#1d4ed8] font-semibold hover:underline" data-testid="signup-to-login">Log In</Link>
      </p>
    </AuthShell>
  );
};

export default Login;
